import { Route } from '@angular/router';
import { InitialDataResolver } from './app.resolvers';
import { AuthGuard } from './auth/guards/auth.guard';
import { LayoutComponent } from './layout/layout.component';
import { NoAuthGuard } from './auth/guards/noAuth.guard';
import { FeatureFlagGuard } from './auth/guards/feature-flag.guard';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/example'
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard',
        data: { title: 'VIP BenefitClub' },
    },

    // Redirect signed in user
    //
    // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        redirectTo: 'dashboard',
        data: { title: 'VIP BenefitClub' },
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-in',
                loadChildren: () => import('./auth/sign-in/sign-in.routing').then(m => m.authSignInRoutes),
                data: { title: 'route.sign_in' },
            },
            {
                path: 'sign-up',
                loadChildren: () => import('./auth/sign-up/sign-up.routing').then(m => m.authSignupRoutes),
                data: { title: 'route.sign_up' },
            },
            {
                path: 'company-sign-up',
                loadChildren: () => import('./auth/company-sign-up/company-sign-up.routing').then(m => m.authSignupRoutes),
                data: { title: 'route.company_sign_up' },
            },
            {
                path: 'confirmation-success',
                loadChildren: () => import('./auth/confirmation-success/confirmation-success.routing').then(m => m.authConfirmationSuccessRoutes),
                data: { title: 'VIP BenefitClub' },
            },
            {
                path: 'pass-change-success',
                loadChildren: () => import('./auth/pass-change-success/pass-change-success.routing').then(m => m.authPassChangeSuccessRoutes),
                data: { title: 'VIP BenefitClub' },
            },
            {
                path: 'confirmation-required',
                loadChildren: () => import('./auth/confirmation-required/confirmation-required.routing').then(m => m.authConfirmationRequiredRoutes),
                data: { title: 'VIP BenefitClub' },
            },
            {
                path: 'forgot-password',
                loadChildren: () => import('./auth/forgot-password/forgot-password.routing').then(m => m.authForgotPasswordRoutes),
                data: { title: 'route.forgot_password' },
            },
            {
                path: 'changepass',
                loadChildren: () => import('./auth/reset-password/reset-password.routing').then(m => m.authResetPasswordRoutes),
                data: { title: 'VIP BenefitClub' },
            },
            {
                path: 'newpass',
                loadChildren: () => import('./auth/new-password/new-password.routing').then(m => m.authNewPasswordRoutes),
                data: { title: 'route.new_password' },
            },
            {
                path: 'privacy',
                loadChildren: () => import('./pages/privacy/privacy.routing').then(m => m.supportRoutes),
                data: { title: 'route.privacy' },
            },
            {
                path: 'resend-link',
                loadChildren: () => import('./auth/resend-link/resend-link.routing').then(m => m.authResendLinkRoutes),
                data: { title: 'route.activation' },
            },
        ],
    },

    // Misc routes (for both states)
    {
        path: '',
        canActivate: [],
        canActivateChild: [],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'unsubscribe-success',
                loadChildren: () => import('./auth/unsubscribe-success/unsubscribe-success.routing').then(m => m.authUnsubscribeSuccessRoutes),
                data: { title: 'VIP BenefitClub' },
            },
            {
                path: 'verify-email',
                loadChildren: () => import('./auth/confirmation-email-success/confirmation-email-success.routing').then(m => m.authConfirmationEmailSuccessRoutes),
                data: { title: 'VIP BenefitClub' },
            },
            {
                path: 'go',
                loadChildren: () => import('./auth/go/go.routing').then(m => m.goRoutes),
                data: { title: 'VIP BenefitClub' },
            },
            {
                path: 'overeni',
                loadChildren: () => import('./verify/verify.routing').then(m => m.verifyRoutes),
                data: { title: 'VIP BenefitClub' },
            },
            {
                path: 'extension-privacy',
                loadChildren: () => import('./pages/extension-privacy/extension-privacy.routing').then(m => m.extensionPrivacyRoutes),
                data: { title: 'Partner Notifier — Privacy' },
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () => import('./auth/sign-out/sign-out.routing').then(m => m.authSignOutRoutes),
                data: { title: 'route.sign_out' },
            },
            {
                path: 'communication-rules',
                loadChildren: () => import('./pages/communication-rules/communication-rules.routing').then(m => m.communicationRulesRoutes),
                data: { title: 'route.communication_rules' },
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,

        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'users',
                loadChildren: () => import('./users/users.routing').then(m => m.usersRoutes),
                data: { title: 'route.users' },
            },
            {
                path: 'unassigned-users',
                loadChildren: () => import('./unassigned-users/unassigned-users.routing').then(m => m.unassignedUsersRoutes),
                data: { title: 'route.unassigned_users' },
            },
            {
                path: 'dashboard',
                loadChildren: () => import('./dashboard-new/dashboard.routing').then(m => m.dashboardRoutes),
                data: { title: 'route.kpi' },
            },
            {
                path: 'support',
                loadChildren: () => import('./pages/support/support.routing').then(m => m.supportRoutes),
                data: { title: 'route.support' },
            },
            {
                path: 'changelog',
                loadChildren: () => import('./pages/changelog/changelog.routing').then(m => m.changelogRoutes),
                data: { title: 'route.changelog' },
            },
            {
                path: 'partners',
                loadChildren: () => import('./partners/partners.routing').then(m => m.partnersRoutes),
                data: { title: 'route.partners' },
            },
            {
                path: 'sellers',
                loadChildren: () => import('./sellers/sellers.routing').then(m => m.sellersRoutes),
                data: { title: 'route.sellers' },
            },
            {
                path: 'profile',
                loadChildren: () => import('./profile/profile.routing').then(m => m.profilesRoutes),
                data: { title: 'route.profile' },
            },
            {
                path: 'categories',
                loadChildren: () => import('./categories/categories.routing').then(m => m.categoriesRoutes),
                data: { title: 'route.categories' },
            },
            {
                path: 'board-categories',
                canActivate: [FeatureFlagGuard],
                loadChildren: () => import('./board-categories/board-categories.routing').then(m => m.boardCategoriesRoutes),
                data: { title: 'route.board_categories', requiredFeatureFlag: 'board_feature' },
            },
            {
                path: 'company-board-categories',
                canActivate: [FeatureFlagGuard],
                // Stejný modul jako /board-categories, jen ve firemním režimu (aktivní firma) – viz board-category-scope.ts
                loadChildren: () => import('./board-categories/board-categories.routing').then(m => m.boardCategoriesRoutes),
                data: { title: 'route.board_categories', requiredFeatureFlag: 'board_feature', boardCategoryScope: 'company' },
            },
            {
                path: 'company-board-posts',
                canActivate: [FeatureFlagGuard],
                // Stejný modul jako /board-posts, jen ve firemním režimu (aktivní firma) – viz board-post-scope.ts
                loadChildren: () => import('./board-posts/board-posts.routing').then(m => m.boardPostsRoutes),
                data: { title: 'route.board_posts', requiredFeatureFlag: 'board_feature', boardPostScope: 'company' },
            },
            {
                path: 'board-posts',
                canActivate: [FeatureFlagGuard],
                loadChildren: () => import('./board-posts/board-posts.routing').then(m => m.boardPostsRoutes),
                data: { title: 'route.board_posts', requiredFeatureFlag: 'board_feature' },
            },
            {
                path: 'primary-types',
                loadChildren: () => import('./primary-types/primary-types.routing').then(m => m.primaryTypesRoutes),
                data: { title: 'route.type_management' },
            },
            {
                path: 'secondary-types',
                loadChildren: () => import('./secondary-types/secondary-types.routing').then(m => m.secondaryTypesRoutes),
                data: { title: 'route.type_management' },
            },
            {
                path: 'taking-benefits',
                loadChildren: () => import('./taking-benefits/taking-benefits.routing').then(m => m.takingBenefitsRoutes),
                data: { title: 'route.taking_benefits' },
            },
            {
                path: 'offer-validity',
                loadChildren: () => import('./offer-validity/offer-validity.routing').then(m => m.offerValidityRoutes),
                data: { title: 'route.offer_validity' },
            },
            {
                path: 'drawing-rules',
                loadChildren: () => import('./drawing-rules/drawing-rules.routing').then(m => m.drawingRulesRoutes),
                data: { title: 'route.drawing_rules' },
            },
            {
                path: 'translates',
                loadChildren: () => import('./translates/translates.routing').then(m => m.translatesRoutes),
                data: { title: 'route.translates' },
            },
            {
                path: 'mobile-settings',
                loadChildren: () => import('./mobile-settings/mobile-settings.routing').then(m => m.mobileSettingsRoutes),
                data: { title: 'route.mobile_settings' },
            },
            {
                path: 'shops',
                loadChildren: () => import('./shops/shops.routing').then(m => m.shopsRoutes),
                data: {
                    title: 'route.shops',
                },
            },
            {
                path: 'specifications',
                loadChildren: () => import('./specifications/specifications.routing').then(m => m.specificationsRoutes),
                data: { title: 'route.specifications' },
            },
            {
                path: 'tickets',
                loadChildren: () => import('./tickets/tickets.routing').then(m => m.ticketsRoutes),
                data: { title: 'route.tickets' },
            },
            {
                path: 'brands',
                loadChildren: () => import('./brands/brands.routing').then(m => m.brandsRoutes),
                data: { title: 'route.brands' },
            },
            {
                path: 'offers',
                loadChildren: () => import('./offers/offers.routing').then(m => m.offersRoutes),
                data: { title: 'route.offers' },
            },
            {
                path: 'approve-brands',
                loadChildren: () => import('./approve-brands/approve-brands.routing').then(m => m.approveBrandsRoutes),
                data: { title: 'route.approve_brands' },
            },
            {
                path: 'approve-company-brands',
                loadChildren: () => import('./approve-company-brands/approve-company-brands.routing').then(m => m.approveCompanyBrandsRoutes),
                data: { title: 'route.approve_company_brands' },
            },
            {
                path: 'approve-drawing-rules',
                loadChildren: () => import('./approve-conditions/approve-conditions.routing').then(m => m.approveConditionsRoutes),
                data: {
                    title: 'route.approve_drawing_rules',
                },
            },
            {
                path: 'approve-taking-benefits',
                loadChildren: () => import('./approve-taking-benefits/approve-taking-benefits.routing').then(m => m.approveTakingBenefitsRoutes),
                data: {
                    title: 'route.approve_taking_benefits',
                },
            },
            {
                path: 'approve-offer-validity',
                loadChildren: () => import('./approve-offer-validity/approve-offer-validity.routing').then(m => m.approveOfferValiditiesRoutes),
                data: {
                    title: 'route.approve_offer_validity',
                },
            },
            {
                path: 'approve-partners',
                loadChildren: () => import('./approve-partner/approve-partners.routing').then(m => m.approvePartnersRoutes),
                data: { title: 'route.approve_partners' },
            },
            {
                path: 'approve-articles',
                loadChildren: () => import('./approve-articles/approve-articles.routing').then(m => m.approveArticlesRoutes),
                data: { title: 'route.approve_articles' },
            },
            {
                path: 'approve-verifications',
                canActivate: [AuthGuard, FeatureFlagGuard],
                loadChildren: () => import('./approve-verifications/approve-verifications.routing').then(m => m.approveVerificationsRoutes),
                data: { title: 'route.approve_verifications', requiredFeatureFlag: 'membership_verification' },
            },
            {
                path: 'approve-offers',
                loadChildren: () => import('./approve-offers/approve-offers.routing').then(m => m.approveOffersRoutes),
                data: { title: 'route.approve_offers' },
            },
            {
                path: 'homepage',
                loadChildren: () => import('./homepage/homepage.routing').then(m => m.homepagesRoutes),
                data: { title: 'route.homepage' },
            },
            {
                path: 'labels',
                loadChildren: () => import('./labels/labels.routing').then(m => m.labelsRoutes),
                data: { title: 'route.labels' },
            },
            {
                path: 'recommendations',
                loadChildren: () => import('./recommendations/recommendations.routing').then(m => m.recommendationsRoutes),
                data: { title: 'route.recommendations' },
            },
            {
                path: 'tags',
                loadChildren: () => import('./tags/tags.routing').then(m => m.tagsRoutes),
                data: { title: 'route.tags' },
            },
            {
                path: 'sponsors',
                loadChildren: () => import('./sponsors/sponsors.routing').then(m => m.sponsorsRouting),
                data: { title: 'route.sponsors' },
            },
            {
                path: 'work-position',
                loadChildren: () => import('./work-position/work-position.routing').then(m => m.workPositionRoutes),
                data: { title: 'route.work_position' },
            },
            {
                path: 'company-unassign-reasons',
                loadChildren: () => import('./company-unassign-reasons/company-unassign-reasons.routing').then(m => m.companyUnassignReasonsRoutes),
                data: { title: 'route.company_unassign_reasons' },
            },
            {
                path: 'documents',
                loadChildren: () => import('./documents/documents.routing').then(m => m.documentsRoutes),
                data: { title: 'route.documents' },
            },
            {
                path: 'ratings',
                loadChildren: () => import('./rating-log/rating-log.routing').then(m => m.ratingsLogRoutes),
                data: { title: 'route.ratings' },
            },
            {
                path: 'reports',
                loadChildren: () => import('./report-log/report-log.routing').then(m => m.reportsLogRoutes),
                data: { title: 'route.reports' },
            },
            {
                path: 'benefit-requests',
                loadChildren: () => import('./benefit-requests/benefit-requests.routing').then(m => m.benefitRequestRoutes),
                data: { title: 'route.benefit_requests' },
            },
            {
                path: 'sign-privacy',
                loadChildren: () => import('./pages/privacy/privacy.routing').then(m => m.supportRoutes),
                data: { title: 'route.privacy' },
            },
            {
                path: 'preferences',
                loadChildren: () => import('./preferences/preferences.routing').then(m => m.preferencesRoutes),
                data: { title: 'route.preferences' },
            },
            {
                path: 'articles',
                loadChildren: () => import('./articles/articles.routing').then(m => m.articlesRoutes),
                data: { title: 'route.articles' },
            },
            {
                path: 'consents',
                loadChildren: () => import('./consents/consents.routing').then(m => m.consentsRoutes),
                data: { title: 'route.consents' },
            },
            {
                path: 'templates',
                loadChildren: () => import('./templates/templates.routing').then(m => m.templatesRoutes),
                data: { title: 'route.templates' },
            },
            {
                // Vizuální editor HTML verze šablony (GrapesJS) – není v menu, otevírá se z detailu šablony.
                path: 'pdf-templates',
                loadChildren: () => import('./pdf-templates/pdf-templates.routing').then(m => m.pdfTemplatesRoutes),
                data: { title: 'route.pdf_templates' },
            },
            {
                path: 'company',
                loadChildren: () => import('./my-company/my-company.routing').then(m => m.myCompanyPartnerRoutes),
                data: { title: 'route.company' },
            },
            {
                path: 'seller',
                loadChildren: () => import('./my-company/my-company.routing').then(m => m.myCompanySellerRoutes),
                data: { title: 'route.seller' },
            },
            {
                path: 'company-redirect',
                loadChildren: () => import('./company/company.routing').then(m => m.companyRoutes),
            },
            {
                path: 'trade-centers',
                loadChildren: () => import('./trade-centers/trade-centers.routing').then(m => m.tradeCentersRoutes),
                data: { title: 'route.trade_centers' },
            },
            {
                path: 'feature-flags',
                loadChildren: () => import('./feature-flags/feature-flags.routing').then(m => m.featureFlagsRoutes),
                data: { title: 'route.feature_flags' },
            },
            {
                path: 'email-domains',
                canActivate: [AuthGuard, FeatureFlagGuard],
                loadChildren: () => import('./email-domains/email-domains.routing').then(m => m.emailDomainsRoutes),
                data: { title: 'route.email_domains', requiredFeatureFlag: 'email_domain_check' },
            },
            {
                path: 'dynamic-acl',
                loadChildren: () => import('./dynamic-acl/dynamic-acl.routing').then(m => m.dynamicAclRoutes),
                data: { title: 'route.dynamic_acl' },
            },

            {
                path: 'notifications',
                canActivate: [AuthGuard, FeatureFlagGuard],
                loadChildren: () => import('./notifications/notifications.routing').then(m => m.notificationsRoutes),
                data: { title: 'route.notifications', requiredFeatureFlag: 'messages' },
            },

            // maintenance, error & Catch all
            {
                path: 'maintenance',
                loadChildren: () => import('./pages/maintenance/maintenance.routing').then(m => m.maintenanceRoutes),
                data: { title: 'VIP BenefitClub' },
            },
            {
                path: '500-internal-server-error',
                loadChildren: () => import('./pages/error/error-500/error-500.routing').then(m => m.error500Routes),
                data: { title: 'VIP BenefitClub' },
            },
            {
                path: '404-not-found',
                pathMatch: 'full',
                loadChildren: () => import('./pages/error/error-404/error-404.routing').then(m => m.error404Routes),
                data: { title: '404 | VIP BenefitClub' },
            },
            {
                path: '**',
                redirectTo: '404-not-found',
                data: { title: '404 | VIP BenefitClub' },
            },
        ],
    },

];
